import { useState, useCallback } from 'react';

// Interface para os metadados do vídeo armazenado
interface StoredVideoMetadata {
  id: string;
  createdAt: string;
  size: number;
  mimeType: string;
  fileType: number;
  publicId?: string;
  additionalData?: Record<string, any>;
}

/**
 * Hook para armazenar vídeos localmente e permitir envios posteriores
 */
const useLocalVideoStorage = () => {
  const [pendingVideos, setPendingVideos] = useState<StoredVideoMetadata[]>([]);

  /**
   * Salva um vídeo no armazenamento local com seus metadados
   */
  const saveVideoLocally = useCallback(async (
    blob: Blob,
    metadata: Omit<StoredVideoMetadata, 'id' | 'createdAt' | 'size'>
  ) => {
    try {
      // Gerar um ID único para o vídeo
      const videoId = `video_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      
      // Criar objeto com os metadados completos
      const videoMetadata: StoredVideoMetadata = {
        id: videoId,
        createdAt: new Date().toISOString(),
        size: blob.size,
        ...metadata
      };
      
      // Converter o blob para base64 (pode ser pesado para vídeos grandes)
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result as string);
      });
      
      // Salvar o vídeo no localStorage
      localStorage.setItem(`stored_video_${videoId}`, base64);
      
      // Salvar os metadados
      const existingMetadata = JSON.parse(localStorage.getItem('pending_videos') || '[]');
      const updatedMetadata = [...existingMetadata, videoMetadata];
      localStorage.setItem('pending_videos', JSON.stringify(updatedMetadata));
      
      // Atualizar o estado
      setPendingVideos(updatedMetadata);
      
      return videoId;
    } catch (error) {
      console.error('Erro ao salvar vídeo localmente:', error);
      return null;
    }
  }, []);

  /**
   * Recupera um vídeo armazenado localmente
   */
  const getStoredVideo = useCallback(async (id: string) => {
    try {
      const base64Content = localStorage.getItem(`stored_video_${id}`);
      if (!base64Content) return null;
      
      // Extrair os metadados
      const allMetadata = JSON.parse(localStorage.getItem('pending_videos') || '[]');
      const metadata = allMetadata.find(meta => meta.id === id);
      
      // Converter base64 para Blob
      const byteString = atob(base64Content.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      const blob = new Blob([ab], { type: metadata.mimeType });
      return { blob, metadata };
    } catch (error) {
      console.error('Erro ao recuperar vídeo:', error);
      return null;
    }
  }, []);

  /**
   * Remove um vídeo do armazenamento local
   */
  const removeStoredVideo = useCallback((id: string) => {
    try {
      // Remover o vídeo
      localStorage.removeItem(`stored_video_${id}`);
      
      // Atualizar os metadados
      const existingMetadata = JSON.parse(localStorage.getItem('pending_videos') || '[]');
      const updatedMetadata = existingMetadata.filter(meta => meta.id !== id);
      localStorage.setItem('pending_videos', JSON.stringify(updatedMetadata));
      
      // Atualizar o estado
      setPendingVideos(updatedMetadata);
      
      return true;
    } catch (error) {
      console.error('Erro ao remover vídeo:', error);
      return false;
    }
  }, []);

  /**
   * Carrega a lista de vídeos pendentes ao inicializar
   */
  const loadPendingVideos = useCallback(() => {
    try {
      const storedMetadata = JSON.parse(localStorage.getItem('pending_videos') || '[]');
      setPendingVideos(storedMetadata);
      return storedMetadata;
    } catch (error) {
      console.error('Erro ao carregar vídeos pendentes:', error);
      return [];
    }
  }, []);

  // Carregar vídeos pendentes na inicialização
  useState(() => {
    loadPendingVideos();
  });

  return {
    pendingVideos,
    saveVideoLocally,
    getStoredVideo,
    removeStoredVideo,
    loadPendingVideos,
    hasPendingVideos: pendingVideos.length > 0
  };
};

export default useLocalVideoStorage; 